(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const formatMoney = (cents, currency = 'EUR') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency', currency
      }).format(cents / 100);
    } catch (_) {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  };

  const showToast = (message) => {
    const toast = document.querySelector('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(window.toastTimer);
    window.toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  const setCartCount = async () => {
    try {
      const cart = await fetch(window.themeRoutes.cartJson, { headers: { Accept: 'application/json' } }).then((response) => response.json());
      document.querySelectorAll('[data-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      });
    } catch (_) {}
  };

  const refreshCartDrawer = async () => {
    const current = document.querySelector('#shopify-section-cart-drawer');
    if (!current) return null;
    const html = await fetch(`${window.location.pathname}?section_id=cart-drawer`).then((response) => response.text());
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.querySelector('#shopify-section-cart-drawer') || parsed.body.firstElementChild;
    if (incoming) current.replaceWith(incoming);
    return document.querySelector('cart-drawer');
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this._keydown = (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this._keydown);
    }

    disconnectedCallback() { document.removeEventListener('keydown', this._keydown); }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      window.setTimeout(() => this.querySelector('[data-drawer-close]')?.focus(), 120);
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      if (!range) return;
      const update = () => this.style.setProperty('--position', `${range.value}%`);
      range.addEventListener('input', update);
      update();
    }
  }
  customElements.define('before-after', BeforeAfter);

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-media-target]');
        if (!thumb) return;
        this.show(thumb.dataset.mediaTarget);
      });
    }

    show(id) {
      const hasTarget = [...this.querySelectorAll('[data-media-id]')].some((media) => media.dataset.mediaId === String(id));
      if (!hasTarget) return;
      this.querySelectorAll('[data-media-id]').forEach((media) => media.classList.toggle('is-active', media.dataset.mediaId === String(id)));
      this.querySelectorAll('[data-media-target]').forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaTarget === String(id)));
    }
  }
  customElements.define('product-gallery', ProductGallery);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.master = this.querySelector('[name="id"]');
      this.submitButton = this.querySelector('[type="submit"]');
      this.variants = JSON.parse(this.querySelector('[data-variants-json]')?.textContent || '[]');
      this.currency = this.dataset.currency || 'EUR';

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-input]')) this.updateVariant();
        if (event.target.matches('[data-style-input]')) this.updateStyle(event.target);
        if (event.target.matches('[data-photo-input]')) this.updateFile(event.target);
      });
      this.addEventListener('click', (event) => {
        const quantityButton = event.target.closest('[data-quantity-change]');
        if (quantityButton) this.changeQuantity(Number(quantityButton.dataset.quantityChange));
        if (event.target.closest('[data-upload-remove]')) this.clearFile();
      });
      this.form?.addEventListener('submit', (event) => this.submit(event));
      this.updateVariant(false);
    }

    selectedOptions() {
      return [...this.querySelectorAll('[data-option-group]')].map((group) => group.querySelector('[data-option-input]:checked')?.value);
    }

    updateVariant(updateUrl = true) {
      if (!this.master || !this.variants.length) return;
      const selected = this.selectedOptions();
      const variant = selected.length
        ? this.variants.find((item) => item.options.every((option, index) => option === selected[index]))
        : this.variants.find((item) => String(item.id) === String(this.master.value)) || this.variants[0];
      if (!variant) {
        this.submitButton.disabled = true;
        this.submitButton.querySelector('[data-button-label]').textContent = 'Combinaison indisponible';
        return;
      }
      this.master.value = variant.id;
      this.querySelectorAll('[data-option-group]').forEach((group) => {
        const value = group.querySelector('[data-option-input]:checked')?.value;
        const selectedLabel = group.querySelector('.product-form__label small');
        if (selectedLabel && value) selectedLabel.textContent = value;
      });
      this.submitButton.disabled = !variant.available;
      this.submitButton.querySelector('[data-button-label]').textContent = variant.available ? this.submitButton.dataset.availableLabel : this.submitButton.dataset.soldoutLabel;
      this.querySelectorAll('[data-current-price]').forEach((element) => element.textContent = formatMoney(variant.price, this.currency));
      this.querySelectorAll('[data-compare-price]').forEach((element) => {
        element.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price, this.currency) : '';
        element.hidden = !(variant.compare_at_price > variant.price);
      });
      if (variant.featured_media?.id) document.querySelector('product-gallery')?.show(variant.featured_media.id);
      if (updateUrl && history.replaceState) history.replaceState({}, '', `${window.location.pathname}?variant=${variant.id}`);
    }

    updateFile(input) {
      const box = input.closest('.upload-box');
      const file = input.files?.[0];
      if (!file) {
        this.clearFile(false);
        return;
      }
      if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
        input.value = '';
        this.clearFile(false);
        showToast('Utilisez une image JPG, PNG ou WEBP.');
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        input.value = '';
        this.clearFile(false);
        showToast('Cette photo dépasse 20 Mo. Choisissez un fichier plus léger.');
        return;
      }
      box?.classList.add('has-file');
      const name = box?.querySelector('[data-file-name]');
      if (name) name.textContent = file.name;
      const preview = this.querySelector('[data-upload-preview]');
      const image = this.querySelector('[data-upload-preview-image]');
      if (preview && image) {
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = URL.createObjectURL(file);
        image.src = this.previewUrl;
        preview.hidden = false;
      }
    }

    clearFile(resetInput = true) {
      const input = this.querySelector('[data-photo-input]');
      const box = input?.closest('.upload-box');
      if (resetInput && input) input.value = '';
      box?.classList.remove('has-file');
      const name = box?.querySelector('[data-file-name]');
      if (name) name.textContent = 'Choisir une photo';
      const preview = this.querySelector('[data-upload-preview]');
      const image = this.querySelector('[data-upload-preview-image]');
      if (preview) preview.hidden = true;
      if (image) image.removeAttribute('src');
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }

    updateStyle(input) {
      const value = this.querySelector('[data-style-value]');
      if (value) value.textContent = input.value;
    }

    changeQuantity(delta) {
      const input = this.querySelector('[data-quantity-input]');
      if (!input) return;
      input.value = Math.max(1, Number(input.value || 1) + delta);
    }

    async submit(event) {
      const photoInput = this.querySelector('[data-photo-input]');
      if (photoInput?.files?.length) {
        if (!this.form?.reportValidity()) event.preventDefault();
        return;
      }
      event.preventDefault();
      if (!this.form?.reportValidity()) return;
      this.submitButton.disabled = true;
      this.submitButton.classList.add('is-loading');
      const label = this.submitButton.querySelector('[data-button-label]');
      const previous = label.textContent;
      label.textContent = 'Ajout en cours…';

      try {
        const response = await fetch(`${window.themeRoutes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Impossible d’ajouter ce portrait.');
        }
        await setCartCount();
        const drawer = await refreshCartDrawer();
        drawer?.open();
        showToast('Votre portrait a bien été ajouté.');
      } catch (error) {
        showToast(error.message || 'Une erreur est survenue. Réessayez.');
      } finally {
        this.submitButton.classList.remove('is-loading');
        this.submitButton.disabled = this.master?.selectedOptions?.[0] ? this.master.selectedOptions[0].disabled : false;
        label.textContent = previous;
      }
    }
  }
  customElements.define('product-form', ProductForm);

  class ChatAssistant extends HTMLElement {
    connectedCallback() {
      this.launcher = this.querySelector('[data-chat-open]');
      this.panel = this.querySelector('[data-chat-panel]');
      this.messages = this.querySelector('[data-chat-messages]');
      this.input = this.querySelector('[data-chat-input]');
      try {
        this.config = JSON.parse(this.querySelector('[data-chat-config]')?.textContent || '{}');
      } catch (_) {
        this.config = { answers: {}, links: {} };
      }

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-chat-open]')) this.toggle();
        if (event.target.closest('[data-chat-close]')) this.close();
        const quick = event.target.closest('[data-chat-intent]');
        if (quick) this.reply(quick.dataset.chatIntent, quick.textContent.trim());
      });
      this.querySelector('[data-chat-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        const question = this.input?.value.trim();
        if (!question) return;
        this.input.value = '';
        this.reply(this.detectIntent(question), question);
      });
      this._keydown = (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this._keydown);

      if (this.dataset.autoOpen === 'true') {
        try {
          if (!sessionStorage.getItem('clicPortraitChatSeen')) {
            window.setTimeout(() => {
              this.open();
              sessionStorage.setItem('clicPortraitChatSeen', '1');
            }, 6500);
          }
        } catch (_) {}
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._keydown);
    }

    open() {
      this.classList.add('is-open');
      this.launcher?.setAttribute('aria-expanded', 'true');
      this.panel?.setAttribute('aria-hidden', 'false');
      window.setTimeout(() => this.input?.focus(), 180);
    }

    close() {
      this.classList.remove('is-open');
      this.launcher?.setAttribute('aria-expanded', 'false');
      this.panel?.setAttribute('aria-hidden', 'true');
      this.launcher?.focus();
    }

    toggle() {
      this.classList.contains('is-open') ? this.close() : this.open();
    }

    detectIntent(value) {
      const text = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      if (/(photo|image|flou|nettet|lumiere|qualite|pixel)/.test(text)) return 'photo';
      if (/(delai|livraison|recevoir|combien de temps|quand|expedi)/.test(text)) return 'delays';
      if (/(format|taille|dimension|style|cadre|toile|affiche|prix|tarif|cout)/.test(text)) return 'formats';
      if (/(retouch|modification|modifier|apercu|validation|changer)/.test(text)) return 'retouch';
      if (/(suivi|commande|colis|tracking|numero de commande|ou est)/.test(text)) return 'tracking';
      return 'contact';
    }

    reply(intent, question) {
      this.addMessage(question, 'user');
      this.querySelector('[data-chat-quick]')?.remove();
      window.setTimeout(() => {
        const answer = this.config?.answers?.[intent] || this.config?.answers?.contact;
        const link = this.config?.links?.[intent] || (intent === 'contact' ? this.config?.links?.contact : null);
        this.addMessage(answer, 'bot', link);
      }, reducedMotion ? 0 : 260);
    }

    addMessage(text, type, link) {
      if (!this.messages || !text) return;
      const message = document.createElement('div');
      message.className = `chat-message chat-message--${type}`;
      if (type === 'bot') {
        const author = document.createElement('span');
        author.className = 'chat-message__author';
        author.textContent = 'Clic Portrait';
        message.append(author);
      }
      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      message.append(paragraph);
      if (link?.url) {
        const anchor = document.createElement('a');
        anchor.href = link.url;
        anchor.textContent = link.label || 'En savoir plus';
        anchor.className = 'chat-message__link';
        message.append(anchor);
      }
      this.messages.append(message);
      this.messages.scrollTo({ top: this.messages.scrollHeight, behavior: reducedMotion ? 'auto' : 'smooth' });
    }
  }
  customElements.define('chat-assistant', ChatAssistant);

  const initReveal = () => {
    const elements = document.querySelectorAll('[data-reveal]');
    if (reducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: .12, rootMargin: '0px 0px -6% 0px' });
    elements.forEach((element) => observer.observe(element));
  };

  const initHeader = () => {
    const header = document.querySelector('[data-header]');
    const menu = document.querySelector('[data-mobile-menu]');
    const toggleScrolled = () => header?.classList.toggle('is-scrolled', window.scrollY > 16);
    toggleScrolled();
    window.addEventListener('scroll', toggleScrolled, { passive: true });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu?.classList.contains('is-open')) {
        menu.classList.remove('is-open');
        menu.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    });
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-mobile-open]')) {
        menu?.classList.add('is-open');
        menu?.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      }
      if (event.target.closest('[data-mobile-close]') || event.target.closest('.mobile-menu__link')) {
        menu?.classList.remove('is-open');
        menu?.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
      if (event.target.closest('[data-cart-open]')) {
        event.preventDefault();
        document.querySelector('cart-drawer')?.open();
      }
    });
  };

  const initStickyBuy = () => {
    const source = document.querySelector('[data-main-buy-button]');
    const sticky = document.querySelector('[data-sticky-buy]');
    if (!source || !sticky || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0 });
    observer.observe(source);
    sticky.querySelector('button')?.addEventListener('click', () => source.click());
  };

  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initHeader();
    initStickyBuy();
  });

  document.addEventListener('shopify:section:load', initReveal);
})();
